// Static Coach mock payloads (Board Coach tab, all-boards Dashboard Coach,
// Goals & motivation workspace). Response shapes mirror the real backend:
// daily hooks consume response.data directly, coachApi unwraps response.data.data.
// Deadlines are computed from Date.now() so buckets always look current.

const HOUR_MS = 3600000;
const DAY_MS = 24 * HOUR_MS;

export const COACH_PLAN_TEXT = `## 🎯 Today's focus
Ship the **pricing page** and unblock the API migration.

### Morning (09:00 – 12:30)
1. **Finalize Q3 budget review** — 90 min (overdue, do it first)
2. Pricing page copy + CTA — 75 min

### Afternoon (13:30 – 17:00)
3. Pair with Sara on the socket migration — 60 min
4. Review PR #482 (auth refresh) — 30 min

> 💡 You planned 5h 15m of focus work against a 6h capacity. Keep a 45m buffer for incident follow-ups.`;

export const COACH_PERF_TEXT = `You closed **12 of 15** planned tasks this week (80%). Deep-work hours are up 15% vs last week, but overdue items cluster on Tuesdays — consider a lighter schedule after standup.`;

export const COACH_SUCCESS_PICTURE =
  "Picture it six months from now: the new site is live, signups grew 12%, and the launch retro is a short, proud meeting. You are showing the graph to the team.";

export const COACH_MOCK_GOALS = [
  {
    refId: "mock-goal-1",
    title: "Launch the new website",
    why: "Refresh our public presence and grow signups.",
    dreamDescription: "A fast, conversion-focused site live by year end.",
    dreamUpdatedAt: new Date(Date.now() - 5 * DAY_MS).toISOString(),
    targetDate: new Date(Date.now() + 60 * DAY_MS).toISOString(),
    metric: "10% signup growth",
    constraints: ["Fixed budget", "Two developers"],
  },
  {
    refId: "mock-goal-2",
    title: "Read 12 books this year",
    why: "Stay sharp and step out of the day-to-day.",
    dreamDescription: "",
    dreamUpdatedAt: null,
    targetDate: null,
    metric: null,
    constraints: [],
  },
];

function mockTaskItem(id, taskName, deadLineMs, extra = {}) {
  return {
    taskId: id,
    taskName,
    deadLineMs,
    status: "Ready",
    complete: 0,
    taskChecklist: [],
    categories: [],
    ...extra,
  };
}

function buildTaskBuckets() {
  const now = Date.now();
  return {
    overdue: [
      mockTaskItem("mock-t-1", "Finalize Q3 budget review", now - 26 * HOUR_MS, { complete: 60 }),
      mockTaskItem("mock-t-2", "Answer the security questionnaire", now - 3 * HOUR_MS, { status: "Reject" }),
      mockTaskItem("mock-t-3", "Update onboarding checklist", now - 2 * DAY_MS, {
        taskMapId: "mock-tm-1",
        taskMapTitle: "Website revamp",
      }),
    ],
    todayTomorrow: [
      mockTaskItem("mock-t-4", "Ship the pricing page copy", now + 3 * HOUR_MS, { complete: 45 }),
      mockTaskItem("mock-t-5", "Review PR #482 (auth refresh)", now + 6 * HOUR_MS),
      mockTaskItem("mock-t-6", "Prepare sprint demo notes", now + 20 * HOUR_MS, {
        taskMapId: "mock-tm-2",
        taskMapTitle: "Mobile app beta",
      }),
    ],
    thisWeek: [
      mockTaskItem("mock-t-7", "Migrate billing to the new API", now + 2 * DAY_MS, { complete: 30 }),
      mockTaskItem("mock-t-8", "1:1 with the design team", now + 4 * DAY_MS),
    ],
    other: [
      mockTaskItem("mock-t-9", "Draft next-year hiring plan", now + 20 * DAY_MS),
      mockTaskItem("mock-t-10", "Renew SSL certificates", now + 45 * DAY_MS),
    ],
  };
}

function buildCompactTaskBuckets() {
  const now = Date.now();
  return {
    overdue: [],
    todayTomorrow: [
      mockTaskItem("mock-t-11", "Fix crash on cold start", now + 5 * HOUR_MS, { complete: 70 }),
    ],
    thisWeek: [
      mockTaskItem("mock-t-12", "Publish TestFlight build", now + 3 * DAY_MS),
    ],
    other: [],
  };
}

function bucketCounts(taskBuckets) {
  return {
    overdueCount: taskBuckets.overdue.length,
    todayTomorrowCount: taskBuckets.todayTomorrow.length,
    thisWeekCount: taskBuckets.thisWeek.length,
    otherCount: taskBuckets.other.length,
  };
}

/** Daily coach doc — same shape as GET /AI/coach/board/:id/daily (+ boardSnapshots for all-boards). */
export function coachDailyDoc({ boardId, dateKey, allBoards = false } = {}) {
  const doc = {
    _id: "mock-coach-daily",
    boardId: boardId ? String(boardId) : null,
    userId: "mock-user",
    reportDateKey: dateKey || null,
    status: "ready",
    taskBuckets: buildTaskBuckets(),
    planText: COACH_PLAN_TEXT,
    performanceLabel: "good",
    performanceText: COACH_PERF_TEXT,
    userSelfScore: null,
    generatedAt: new Date().toISOString(),
    regenerateCount: 0,
    maxRegeneratesPerDay: 5,
  };
  if (allBoards) {
    const bucketsA = buildTaskBuckets();
    const bucketsB = buildCompactTaskBuckets();
    doc.boardSnapshots = [
      { boardId: "mock-board-1", boardName: "Website Revamp", reportDateKey: dateKey || null, ...bucketCounts(bucketsA), taskBuckets: bucketsA },
      { boardId: "mock-board-2", boardName: "Mobile App Beta", reportDateKey: dateKey || null, ...bucketCounts(bucketsB), taskBuckets: bucketsB },
    ];
  }
  return doc;
}

/** GET /AI/coach/user/all-boards/daily/generation-context payload. */
export function coachGenerationContext({ boardId, dateKey, allBoards = false } = {}) {
  return {
    ...(boardId ? { boardId: String(boardId) } : {}),
    userId: "mock-user",
    boards: allBoards ? [{ boardId: "mock-board-1" }, { boardId: "mock-board-2" }] : [],
    taskBuckets: buildTaskBuckets(),
    coachLanguage: "en",
    dailyWorkMinutes: 360,
    dateKey: dateKey || new Date().toISOString().slice(0, 10),
    previousDocs: [],
    personalGoals: COACH_MOCK_GOALS,
    personalGoalRefs: COACH_MOCK_GOALS.map((goal) => goal.refId),
  };
}

/** GET /AI/coach/summary payload (used by the digest strip). */
export function coachSummary() {
  return {
    counts: { messages: 2, pathLinks: 3, roleModels: 2, learning: 4, inspiration: 5, habits: 3 },
    generatedAt: new Date().toISOString(),
  };
}

/** Seed for the proactive initiative inbox (GET /AI/coach/initiatives). */
export function coachInitiatives() {
  return [
    {
      _id: "mock-init-1",
      kind: "goal_check",
      scopeKind: "allBoards",
      title: "How is the website launch going?",
      body: 'You planned 3 tasks for the "Launch the new website" goal this week. Two are still open — want to review the plan together?',
      status: "ready",
      chatId: null,
      evidence: [],
      readyAt: new Date(Date.now() - 2 * HOUR_MS).toISOString(),
    },
    {
      _id: "mock-init-2",
      kind: "win",
      scopeKind: "personal",
      title: "Nice streak — 5 days of deep work 🎉",
      body: "You completed your focus block 5 days in a row. Keep the momentum and consider blocking tomorrow morning too.",
      status: "ready",
      chatId: null,
      evidence: [],
      readyAt: new Date(Date.now() - 26 * HOUR_MS).toISOString(),
    },
    {
      _id: "mock-init-3",
      kind: "nudge",
      scopeKind: "personal",
      title: "Two tasks are slipping",
      body: '"Finalize Q3 budget review" is overdue since yesterday. Want to reschedule it or split it into smaller steps?',
      status: "read",
      chatId: null,
      evidence: [],
      readyAt: new Date(Date.now() - 3 * DAY_MS).toISOString(),
    },
  ];
}

/** Raw GET /AI/coach/workspace payload — passes through normalizeWorkspace. */
export function coachWorkspaceRaw(scope = { kind: "allBoards" }) {
  const rec = (kind, { title, rationale, resource }) => ({
    id: `mock-rec-${kind}`,
    kind,
    goalRef: "mock-goal-1",
    goalTitle: COACH_MOCK_GOALS[0].title,
    title,
    rationale,
    params: {},
    curated: true,
    aiGenerated: false,
    ...(resource ? { resource } : {}),
  });
  return {
    generatedAt: new Date().toISOString(),
    scope,
    goals: COACH_MOCK_GOALS,
    recommendations: {
      inspiration: [
        rec("inspiration", {
          title: "Start with why — Simon Sinek",
          rationale: "Reconnect the website launch with the bigger picture.",
          resource: { type: "video", provider: "YouTube", url: "https://www.youtube.com/watch?v=u4ZoJKF_VuA", isSearch: true },
        }),
      ],
      learning: [
        rec("learning", {
          title: "Deep Work — Cal Newport",
          rationale: "Your focus blocks shrink on meeting-heavy days.",
          resource: { type: "link", provider: "Web", url: "https://calnewport.com/writing/", isSearch: true },
        }),
      ],
      roleModels: [
        rec("role_model", {
          title: "Study how Julie Zhuo runs product teams",
          rationale: "She ships fast with small teams, like yours.",
          resource: { type: "link", provider: "Web", url: "https://jszoo.com/", isSearch: true },
        }),
      ],
      habits: [
        rec("habit", {
          title: "Write 20 minutes every morning",
          action: "build",
          rationale: "Attach it to your morning coffee; small and daily beats long and rare.",
        }),
      ],
      successPath: [
        {
          id: "mock-person-1",
          name: "Paul Graham",
          role: "Essayist & founder",
          links: [
            {
              title: "How to do great work",
              resource: { type: "link", provider: "Web", url: "https://paulgraham.com/greatwork.html", isSearch: false },
            },
          ],
        },
      ],
    },
    progress: { "mock-goal-1": { activeTasks: 4, tasksCompletedLast7: 6 } },
    officialVideos: [],
    curated: { status: "ready" },
    feedback: {},
    preferences: { tone: "supportive", language: "en" },
    capabilities: {},
    organizationalGoals: [],
  };
}

/** Fresh items for POST /AI/coach/recommendations/reanalyze ([] for successPath). */
export function coachSectionItems({ kind, goalRef, goalTitle } = {}) {
  if (kind === "successPath") return [];
  const base = {
    goalRef: goalRef || null,
    goalTitle: goalTitle || null,
    curated: true,
    aiGenerated: true,
  };
  if (kind === "habit") {
    return [{
      ...base,
      id: `mock-habit-${Date.now()}`,
      kind: "habit",
      title: "Review tomorrow's plan each evening",
      action: "build",
      rationale: "A 5-minute evening review keeps the morning plan realistic.",
      params: {},
    }];
  }
  const fresh = {
    inspiration: {
      title: "Watch a founder teardown of a great landing page",
      rationale: "Freshly generated to match your current goal progress.",
      resource: { type: "video", provider: "YouTube", url: "https://www.youtube.com/watch?v=IxHn8TFeA0s", isSearch: true },
    },
    learning: {
      title: "Shape Up — 6-week cycles",
      rationale: "Your launch slips when scopes grow mid-cycle.",
      resource: { type: "link", provider: "Web", url: "https://basecamp.com/shapeup", isSearch: true },
    },
    roleModels: {
      title: "Follow how the Linear team communicates scope",
      rationale: "Tight scope communication fits your two-person team.",
      resource: { type: "link", provider: "Web", url: "https://linear.app/method", isSearch: true },
    },
  };
  const picked = fresh[kind] || fresh.learning;
  return [{
    ...base,
    id: `mock-${kind}-${Date.now()}`,
    kind,
    title: picked.title,
    rationale: picked.rationale,
    params: {},
    resource: picked.resource,
  }];
}
